import { useState, useEffect, useRef } from 'react';
import { BarChart3, TrendingUp, Clock, CheckCircle, Eye, Calendar, Users, FileText, Activity, Download } from 'lucide-react';

const ReportsAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [reviewAnalytics, setReviewAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('analytics'); // 'analytics' or 'reports'
  const reportRef = useRef(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Fetch dashboard stats
      const dashboardResponse = await fetch('/api/ideas/get_dashboard_stats');
      const dashboardData = await dashboardResponse.json();

      // Fetch review analytics
      const reviewResponse = await fetch('/api/ideas/get_review_analytics');
      const reviewData = await reviewResponse.json();

      // Fetch all ideas for detailed analysis
      const ideasResponse = await fetch('/api/ideas/get_ideas?limit=1000&offset=0');
      const ideasData = await ideasResponse.json();

      setAnalyticsData({
        dashboard: dashboardData,
        ideas: ideasData.ideas || []
      });
      setReviewAnalytics(reviewData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analyticsData) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600 font-medium">Failed to load analytics data</p>
        </div>
      </div>
    );
  }

  const { dashboard, ideas } = analyticsData;

  // Calculate additional metrics
  const totalIdeas = dashboard["Total Ideas"] || 0;
  const pendingReview = dashboard["Pending Reviews"] || 0;
  const underReview = dashboard["Under Review"] || 0;
  const approvedIdeas = dashboard["Approved Ideas"] || 0;
  const approvalRate = dashboard["Percent of Ideas Approved"] || 0;
  const medianReviewTime = dashboard["Average Review Time (days)"] || 0;

  // Status breakdown
  const statusBreakdown = [
    { status: 'Submitted', count: pendingReview, color: 'var(--cares-amber)', percentage: (pendingReview / totalIdeas * 100).toFixed(1) },
    { status: 'Under Review', count: underReview, color: 'var(--cares-teal)', percentage: (underReview / totalIdeas * 100).toFixed(1) },
    { status: 'Approved', count: approvedIdeas, color: 'var(--cares-green)', percentage: (approvedIdeas / totalIdeas * 100).toFixed(1) },
    { status: 'Rejected', count: totalIdeas - pendingReview - underReview - approvedIdeas, color: 'var(--cares-orange)', percentage: ((totalIdeas - pendingReview - underReview - approvedIdeas) / totalIdeas * 100).toFixed(1) }
  ];

  // Submission trends (group by month)
  const submissionTrends = {};
  ideas.forEach(idea => {
    if (idea.submission_date) {
      const month = idea.submission_date.substring(0, 7); // YYYY-MM
      submissionTrends[month] = (submissionTrends[month] || 0) + 1;
    }
  });

  const sortedMonths = Object.keys(submissionTrends).sort();
  const last6Months = sortedMonths.slice(-6);

  return (
    <div className="p-8 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen" ref={reportRef}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <BarChart3 className="w-10 h-10 text-blue-600" />
              Reports & Analytics
            </h1>
            <p className="text-gray-600 text-lg">Comprehensive insights into your AIEA system</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAnalyticsData}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
            >
              <Activity className="w-5 h-5" />
              Refresh Data
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-300">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'analytics'
                ? 'border-b-4 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📊 Analytics Dashboard
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-6 py-3 font-semibold transition-all ${
              activeTab === 'reports'
                ? 'border-b-4 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📄 Generate Reports
          </button>
        </div>
      </div>

      {/* Analytics Tab Content */}
      {activeTab === 'analytics' && (
        <>
          {/* Key Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Total Ideas"
          value={totalIdeas}
          icon={FileText}
          color="blue"
          subtitle="All time submissions"
        />
        <MetricCard
          title="Approval Rate"
          value={`${approvalRate.toFixed(1)}%`}
          icon={CheckCircle}
          color="green"
          subtitle={`${approvedIdeas} approved`}
        />
        <MetricCard
          title="Median Review Time"
          value={`${medianReviewTime} days`}
          icon={Clock}
          color="purple"
          subtitle="Time to approval"
        />
        <MetricCard
          title="Pending Review"
          value={pendingReview + underReview}
          icon={Eye}
          color="orange"
          subtitle={`${pendingReview} new, ${underReview} in progress`}
        />
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Status Distribution */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Status Distribution
          </h2>
          <div className="space-y-4">
            {statusBreakdown.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-gray-700">{item.status}</span>
                  <span className="text-sm font-bold" style={{ color: item.color }}>
                    {item.count} ({item.percentage}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${item.percentage}%`,
                      backgroundColor: item.color
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submission Trends */}
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-600" />
            Submission Trends (Last 6 Months)
          </h2>
          <div className="space-y-3">
            {last6Months.map((month, index) => {
              const count = submissionTrends[month];
              const maxCount = Math.max(...Object.values(submissionTrends));
              const percentage = (count / maxCount * 100);
              return (
                <div key={index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{month}</span>
                    <span className="text-sm font-bold text-blue-600">{count} ideas</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Review Analytics */}
      {reviewAnalytics && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Review Time Analytics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
              <div className="text-3xl font-bold text-blue-600 mb-1">
                {reviewAnalytics.summary.median_days}
              </div>
              <div className="text-sm text-gray-600 font-medium">Median Days</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
              <div className="text-3xl font-bold text-purple-600 mb-1">
                {reviewAnalytics.summary.average_days}
              </div>
              <div className="text-sm text-gray-600 font-medium">Average Days</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-200">
              <div className="text-3xl font-bold text-green-600 mb-1">
                {reviewAnalytics.summary.min_days}
              </div>
              <div className="text-sm text-gray-600 font-medium">Fastest (days)</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-200">
              <div className="text-3xl font-bold text-red-600 mb-1">
                {reviewAnalytics.summary.max_days}
              </div>
              <div className="text-sm text-gray-600 font-medium">Slowest (days)</div>
            </div>
          </div>

          {/* Percentiles */}
          <div className="mt-6 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
            <h3 className="font-bold text-gray-900 mb-3">📊 Percentiles</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-indigo-600">{reviewAnalytics.summary.median_days}</div>
                <div className="text-xs text-gray-600">50th Percentile</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-indigo-600">{reviewAnalytics.summary.p75_days}</div>
                <div className="text-xs text-gray-600">75th Percentile</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-indigo-600">{reviewAnalytics.summary.p90_days}</div>
                <div className="text-xs text-gray-600">90th Percentile</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Top Submitters */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <Users className="w-6 h-6 text-blue-600" />
          Top Idea Submitters
        </h2>
        <TopSubmittersTable ideas={ideas} />
      </div>
        </>
      )}

      {/* Reports Generation Tab Content */}
      {activeTab === 'reports' && (
        <ReportGenerationSection analyticsData={analyticsData} reviewAnalytics={reviewAnalytics} />
      )}
    </div>
  );
};

// MetricCard Component - Matches Dashboard StatsCard styling
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => {
  const colorMap = {
    blue: '#3b82f6',
    green: '#10b981',
    purple: '#8b5cf6',
    orange: '#f59e0b',
    red: '#ef4444',
    cyan: '#06b6d4'
  };

  const bgColor = colorMap[color] || '#3b82f6';

  return (
    <div
      style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        border: '1px solid var(--cares-line-card)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 10px 15px rgba(0,0,0,0.15)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
      }}
    >
      <div>
        <p style={{
          fontSize: '14px',
          color: 'var(--cares-text-label)',
          fontWeight: '600',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          {title}
        </p>
        <p style={{
          fontSize: '32px',
          fontWeight: 'bold',
          color: 'var(--cares-text-1)',
          marginBottom: '4px'
        }}>
          {value}
        </p>
        {subtitle && (
          <p style={{
            fontSize: '12px',
            color: 'var(--cares-text-label)',
            marginTop: '4px'
          }}>
            {subtitle}
          </p>
        )}
      </div>
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '12px',
        background: `${bgColor}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}>
        <Icon style={{ width: '28px', height: '28px', color: bgColor }} />
      </div>
    </div>
  );
};

// TopSubmittersTable Component
const TopSubmittersTable = ({ ideas }) => {
  const submitterCounts = {};
  ideas.forEach(idea => {
    const submitter = idea.submitted_by || 'Unknown';
    submitterCounts[submitter] = (submitterCounts[submitter] || 0) + 1;
  });

  const topSubmitters = Object.entries(submitterCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Rank</th>
            <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Submitter</th>
            <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Ideas Submitted</th>
            <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Contribution</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {topSubmitters.map(([submitter, count], index) => {
            const percentage = (count / ideas.length * 100).toFixed(1);
            return (
              <tr key={index} className="hover:bg-blue-50 transition-colors">
                <td className="px-6 py-4">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 font-bold text-sm">
                    {index + 1}
                  </span>
                </td>
                <td className="px-6 py-4 font-medium text-gray-900">{submitter}</td>
                <td className="px-6 py-4 text-gray-700 font-semibold">{count}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                      <div
                        className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-semibold text-blue-600">{percentage}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Report Generation Section Component
const ReportGenerationSection = ({ analyticsData, reviewAnalytics }) => {
  const [generatingId, setGeneratingId] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);

  const viewReport = (reportId) => {
    setViewingReport(reportId);
  };

  const closeModal = () => {
    setViewingReport(null);
  };

  const generatePDF = async (reportId) => {
    setGeneratingId(reportId);
    // Simulate report generation
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Trigger print dialog for PDF generation
    window.print();
    setGeneratingId(null);
  };

  const reports = [
    {
      id: 'executive-summary',
      title: 'Executive Summary Report',
      description: 'High-level overview of ideas management performance with key metrics and trends',
      icon: '📊',
      color: 'blue'
    },
    {
      id: 'detailed-analytics',
      title: 'Detailed Analytics Report',
      description: 'Comprehensive analysis including all charts, tables, and statistical breakdowns',
      icon: '📈',
      color: 'green'
    },
    {
      id: 'submitter-performance',
      title: 'Submitter Performance Report',
      description: 'Individual contributor analysis with submission rates and approval statistics',
      icon: '👥',
      color: 'purple'
    },
    {
      id: 'review-efficiency',
      title: 'Review Efficiency Report',
      description: 'Review process metrics including turnaround times and bottleneck analysis',
      icon: '⏱️',
      color: 'orange'
    },
    {
      id: 'trend-analysis',
      title: 'Trend Analysis Report',
      description: 'Month-over-month comparisons and forecasting based on historical data',
      icon: '📉',
      color: 'cyan'
    },
    {
      id: 'custom-report',
      title: 'Custom Report Builder',
      description: 'Build your own report by selecting specific metrics and date ranges',
      icon: '🔧',
      color: 'indigo'
    }
  ];

  const colorMap = {
    blue: '#3b82f6',
    green: '#10b981',
    purple: '#8b5cf6',
    orange: '#f59e0b',
    cyan: '#06b6d4',
    indigo: '#6366f1'
  };

  return (
    <div className="space-y-6">
      {/* Report Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reports.map((report) => {
          const bgColor = colorMap[report.color] || '#3b82f6';

          return (
            <div
              key={report.id}
              style={{
                background: 'var(--cares-surface-card)',
                borderRadius: '16px',
                padding: '24px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                border: '1px solid var(--cares-line-card)',
                transition: 'all 0.2s',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 10px 15px rgba(0,0,0,0.15)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '12px',
                  background: `${bgColor}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px'
                }}>
                  {report.icon}
                </div>
              </div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--cares-text-1)',
                marginBottom: '8px'
              }}>
                {report.title}
              </h3>
              <p style={{
                fontSize: '14px',
                color: 'var(--cares-text-label)',
                marginBottom: '16px',
                flexGrow: 1,
                minHeight: '60px'
              }}>
                {report.description}
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => viewReport(report.id)}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: 'var(--cares-surface-card)',
                    color: bgColor,
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: `2px solid ${bgColor}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = `${bgColor}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--cares-surface-card)';
                  }}
                >
                  <Eye style={{ width: '16px', height: '16px' }} />
                  View Report
                </button>
                <button
                  onClick={() => generatePDF(report.id)}
                  disabled={generatingId === report.id}
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    background: bgColor,
                    color: 'var(--cares-text-max)',
                    borderRadius: '8px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: generatingId === report.id ? 'not-allowed' : 'pointer',
                    opacity: generatingId === report.id ? 0.5 : 1,
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (generatingId !== report.id) e.currentTarget.style.opacity = '0.9';
                  }}
                  onMouseLeave={(e) => {
                    if (generatingId !== report.id) e.currentTarget.style.opacity = '1';
                  }}
                >
                  {generatingId === report.id ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid var(--cares-line-card)',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <Download style={{ width: '16px', height: '16px' }} />
                      PDF
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Report Modal */}
      {viewingReport && (
        <ReportModal
          reportId={viewingReport}
          onClose={closeModal}
          analyticsData={analyticsData}
          reviewAnalytics={reviewAnalytics}
        />
      )}
    </div>
  );
};

// Report Modal Component
const ReportModal = ({ reportId, onClose, analyticsData, reviewAnalytics }) => {
  const reportRef = useRef(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--cares-surface-card)',
          borderRadius: '16px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid var(--cares-line-card)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          background: 'var(--cares-surface-card)',
          zIndex: 10,
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px'
        }}>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--cares-text-1)', margin: 0 }}>
            {reportId === 'executive-summary' && '📊 Executive Summary Report'}
            {reportId === 'detailed-analytics' && '📈 Detailed Analytics Report'}
            {reportId === 'submitter-performance' && '👥 Submitter Performance Report'}
            {reportId === 'review-efficiency' && '⏱️ Review Efficiency Report'}
            {reportId === 'trend-analysis' && '📉 Trend Analysis Report'}
            {reportId === 'custom-report' && '🔧 Custom Report Builder'}
          </h2>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handlePrint}
              style={{
                padding: '10px 20px',
                background: 'var(--cares-blue)',
                color: 'var(--cares-text-max)',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Download style={{ width: '16px', height: '16px' }} />
              Export PDF
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: 'var(--cares-orange)',
                color: 'var(--cares-text-max)',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div ref={reportRef} style={{ padding: '32px' }}>
          {reportId === 'executive-summary' && (
            <ExecutiveSummaryReport analyticsData={analyticsData} reviewAnalytics={reviewAnalytics} />
          )}
          {reportId !== 'executive-summary' && (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--cares-text-label)' }}>
              <p style={{ fontSize: '18px', marginBottom: '12px' }}>🚧 Report Under Development</p>
              <p>This report template will be available soon.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Executive Summary Report Component
const ExecutiveSummaryReport = ({ analyticsData, reviewAnalytics }) => {
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingAI, setLoadingAI] = useState(true);

  useEffect(() => {
    // Fetch AI-generated executive summary from Claude API
    const fetchAISummary = async () => {
      try {
        setLoadingAI(true);
        // Was an absolute URL to localhost:8000 — nothing listens there, so the
        // AI executive summary never rendered in any environment. Relative path
        // goes through the same proxy as every other call on this screen.
        const response = await fetch('/api/reports/generate_executive_summary');
        const data = await response.json();
        setAiSummary(data);
      } catch (error) {
        console.error('Error fetching AI summary:', error);
      } finally {
        setLoadingAI(false);
      }
    };

    fetchAISummary();
  }, []);

  if (loadingAI) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid var(--cares-line-card)',
          borderTopColor: 'var(--cares-blue)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <p style={{ fontSize: '16px', color: 'var(--cares-text-label)' }}>Generating AI-powered executive summary...</p>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-note)', marginTop: '8px' }}>Analyzing data with Claude AI</p>
      </div>
    );
  }

  if (!aiSummary) {
    return <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--cares-orange)' }}>
      Failed to generate executive summary. Please try again.
    </div>;
  }

  const { statistics, trends, review_times, top_submitters, ai_summary } = aiSummary;
  const totalIdeas = statistics.total_ideas || 0;
  const approvedIdeas = statistics.approved_ideas || 0;
  const pendingReview = statistics.pending_ideas || 0;
  const underReview = statistics.under_review_ideas || 0;
  const approvalRate = statistics.approval_rate || 0;
  const medianReviewTime = statistics.avg_review_days || 0;

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Report Header */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '8px' }}>
          Executive Summary Report
        </h1>
        <p style={{ fontSize: '16px', color: 'var(--cares-text-label)' }}>
          Ideas Management System Performance Overview
        </p>
        <p style={{ fontSize: '14px', color: 'var(--cares-text-note)', marginTop: '8px' }}>
          Generated on {currentDate}
        </p>
      </div>

      {/* Key Metrics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <MetricBox title="Total Ideas Submitted" value={totalIdeas} color="var(--cares-blue)" />
        <MetricBox title="Approval Rate" value={`${approvalRate.toFixed(1)}%`} color="var(--cares-green)" />
        <MetricBox title="Median Review Time" value={`${medianReviewTime} days`} color="var(--cares-purple)" />
        <MetricBox title="Pending Review" value={pendingReview + underReview} color="var(--cares-amber)" />
      </div>

      {/* AI-Generated Executive Overview */}
      {ai_summary && ai_summary.executive_overview && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            🤖 AI Executive Overview
            <span style={{ fontSize: '12px', fontWeight: 'normal', color: 'var(--cares-blue)', background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))', padding: '4px 12px', borderRadius: '12px' }}>
              Powered by Claude
            </span>
          </h2>
          <div style={{
            background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
            padding: '24px',
            borderRadius: '12px',
            color: 'var(--cares-text-max)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>
            <p style={{ fontSize: '15px', lineHeight: '1.7', margin: 0 }}>
              {ai_summary.executive_overview}
            </p>
          </div>
        </div>
      )}

      {/* AI-Generated Key Insights */}
      {ai_summary && ai_summary.key_insights && ai_summary.key_insights.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            💡 Key Insights
          </h2>
          <div style={{
            background: 'color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid var(--cares-amber)'
          }}>
            <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--cares-amber)' }}>
              {ai_summary.key_insights.map((insight, index) => (
                <li key={index} style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6' }}>
                  <strong>{insight}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* AI-Generated Performance Analysis */}
      {ai_summary && ai_summary.performance_analysis && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            📊 Performance Analysis
          </h2>
          <div style={{
            background: 'color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--cares-green)', lineHeight: '1.7', margin: 0 }}>
              {ai_summary.performance_analysis}
            </p>
          </div>
        </div>
      )}

      {/* Review Time Analytics */}
      {review_times && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            ⏱️ Review Time Analytics
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '16px'
          }}>
            <StatBox label="Fastest Review" value={`${review_times.min_days.toFixed(1)} days`} color="var(--cares-green)" />
            <StatBox label="Median Review" value={`${review_times.median_days.toFixed(1)} days`} color="var(--cares-blue)" />
            <StatBox label="Slowest Review" value={`${review_times.max_days.toFixed(1)} days`} color="var(--cares-orange)" />
          </div>
        </div>
      )}

      {/* AI-Generated Trends & Patterns */}
      {ai_summary && ai_summary.trends_patterns && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            📈 Trends & Patterns
          </h2>
          <div style={{
            background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid var(--cares-amber)'
          }}>
            <p style={{ fontSize: '14px', color: 'var(--cares-amber)', lineHeight: '1.7', margin: 0 }}>
              {ai_summary.trends_patterns}
            </p>
          </div>
        </div>
      )}

      {/* AI-Generated Recommendations */}
      {ai_summary && ai_summary.recommendations && ai_summary.recommendations.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            🎯 Strategic Recommendations
          </h2>
          <div style={{
            background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
            padding: '20px',
            borderRadius: '12px',
            border: '2px solid var(--cares-blue)'
          }}>
            <ul style={{ margin: 0, paddingLeft: '20px', color: 'var(--cares-blue)' }}>
              {ai_summary.recommendations.map((rec, index) => (
                <li key={index} style={{ marginBottom: '12px', fontSize: '14px', lineHeight: '1.6' }}>
                  <strong>{rec}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* AI-Generated Outlook */}
      {ai_summary && ai_summary.outlook && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
            🔮 Outlook
          </h2>
          <div style={{
            background: 'linear-gradient(135deg, var(--cares-purple) 0%, var(--cares-purple) 100%)',
            padding: '20px',
            borderRadius: '12px',
            color: 'var(--cares-text-max)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
          }}>
            <p style={{ fontSize: '14px', lineHeight: '1.7', margin: 0 }}>
              {ai_summary.outlook}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper Components for Executive Summary
const MetricBox = ({ title, value, color }) => (
  <div style={{
    background: 'var(--cares-surface-card)',
    padding: '20px',
    borderRadius: '12px',
    border: '2px solid var(--cares-line-card)',
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '32px', fontWeight: 'bold', color: color, marginBottom: '8px' }}>
      {value}
    </div>
    <div style={{ fontSize: '14px', color: 'var(--cares-text-label)', fontWeight: '600' }}>
      {title}
    </div>
  </div>
);

const StatBox = ({ label, value, color }) => (
  <div style={{
    background: `${color}10`,
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${color}30`,
    textAlign: 'center'
  }}>
    <div style={{ fontSize: '24px', fontWeight: 'bold', color: color, marginBottom: '4px' }}>
      {value}
    </div>
    <div style={{ fontSize: '12px', color: 'var(--cares-text-label)' }}>
      {label}
    </div>
  </div>
);

// Add print styles and animations
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }

    @media print {
      body {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }

      .print\\:hidden {
        display: none !important;
      }

      /* Ensure proper page breaks */
      .bg-white {
        page-break-inside: avoid;
      }

      /* Optimize for print */
      * {
        box-shadow: none !important;
      }

      /* Make sure gradients print */
      .bg-gradient-to-br,
      .bg-gradient-to-r {
        print-color-adjust: exact;
        -webkit-print-color-adjust: exact;
      }
    }
  `;
  if (!document.head.querySelector('style[data-reports-styles]')) {
    style.setAttribute('data-reports-styles', 'true');
    document.head.appendChild(style);
  }
}

export default ReportsAnalytics;
